import type { ProgrammeState, RepwiseEvent } from '@repwise/domain';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNow, useRepo, useSyncStatus } from '../lib/hooks.ts';
import { dataNudge, LAST_EXPORT_KEY } from '../lib/reliability.ts';

/** One quiet banner when data is at risk (US-18, J11). Never shown during a live session. */
export function DataNudge({ programme, events }: { programme: ProgrammeState; events: RepwiseEvent[] }) {
  const repo = useRepo();
  const status = useSyncStatus();
  const now = useNow(15_000); // keeps "retrying in 4 min" current
  const [busy, setBusy] = useState(false);
  const [lastExportAt, setLastExportAt] = useState<number | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    void repo.store.meta<number>(LAST_EXPORT_KEY).then((v) => alive && setLastExportAt(v), () => undefined);
    return () => {
      alive = false;
    };
  }, [repo, events]);
  const firstUseAt = events.find((e) => e.type === 'programme_started')?.at;
  const hasWorkouts = programme.completed > 0 || events.some((e) => e.type === 'workout_imported');
  const n = dataNudge({ status, now, syncConfigured: !!repo.auth, ...(lastExportAt !== undefined ? { lastExportAt } : {}), hasWorkouts, ...(firstUseAt !== undefined ? { firstUseAt } : {}) });
  if (!n) return null;
  return (
    // SY01 / DS01 finals (Wave 2.4 batch 4): one rounded line, a glyph for the kind, and a pill for its action
    <div className={`mx-3 mt-2 rounded-[14px] text-sm p-3 flex justify-between items-center gap-3 bg-surface-overlay max-w-[480px] sm:mx-auto ${n.kind === 'attention' ? 'text-sync-warn' : ''}`} role="status">
      <span className="flex items-start gap-2">
        <span aria-hidden="true" className={`shrink-0 ${n.kind === 'attention' ? '' : 'text-text-muted'}`}>
          {n.kind === 'attention' ? '!' : n.kind === 'retry' ? '↻' : n.kind === 'pending' ? '☁' : '⛁'}
        </span>
        <span>{n.text}</span>
      </span>
      {n.kind === 'retry' ? (
        <button type="button" disabled={busy} className="text-now-ink min-h-11 px-3 rounded-full bg-surface-sunken shrink-0" onClick={() => (setBusy(true), void repo.engine.retryNow('manual').finally(() => setBusy(false)))}>
          {busy ? 'Syncing…' : 'Sync now'}
        </button>
      ) : (
        <Link to={n.to} className="text-now-ink min-h-11 px-3 rounded-full bg-surface-sunken grid place-items-center shrink-0">
          {n.kind === 'attention' ? 'Review' : n.kind === 'backup' && !repo.auth ? 'Back up' : 'Details'}
        </Link>
      )}
    </div>
  );
}
